<template>
  <div class="viq-timeline">
    <div class="viq-timeline__header">
      <h3 class="viq-timeline__title">Regulatory Timeline</h3>
      <el-button v-if="!started" size="small" @click="goBack">Back</el-button>
    </div>

    <div v-if="!started" class="viq-timeline__form">
      <p class="viq-timeline__desc">
        Generate a chronological timeline of regulatory events for a topic or jurisdiction.
      </p>
      <el-input
        v-model="topic"
        type="textarea"
        :rows="3"
        placeholder="e.g., PSD2/PSD3 regulatory timeline for the EU..."
      />
      <div class="viq-timeline__form-row">
        <el-input v-model="jurisdiction" size="small" placeholder="Jurisdiction (optional)" />
        <el-button type="primary" :disabled="!topic.trim()" @click="generate">
          Generate Timeline
        </el-button>
      </div>
    </div>

    <div v-else class="viq-timeline__events">
      <ViqTimelineEvent
        v-for="(event, i) in timelineEvents"
        :key="i"
        :event="event"
        :is-last="i === timelineEvents.length - 1"
      />
      <div v-if="isStreaming" class="viq-timeline__loading">
        <span class="viq-timeline__spinner" />
        Generating timeline...
      </div>
      <div v-if="!isStreaming && timelineEvents.length === 0" class="viq-timeline__empty">
        No timeline events found.
      </div>
    </div>

    <div v-if="!isStreaming && started" class="viq-timeline__actions">
      <el-button type="primary" @click="exportPdf">Export as PDF</el-button>
      <el-button v-if="$config.features && $config.features.viqShare" @click="share">Save &amp; Share</el-button>
      <el-button @click="reset">New Timeline</el-button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ViqTimeline",
  components: {
    ViqTimelineEvent: () => import("./ViqTimelineEvent"),
  },
  data() {
    return {
      topic: "",
      jurisdiction: "",
      started: false,
    };
  },
  computed: {
    ...mapState("Viq", ["timelineEvents", "isStreaming"]),
  },
  methods: {
    generate() {
      if (!this.topic.trim()) return;
      this.started = true;
      this.$store.dispatch("Viq/streamTimeline", {
        topic: this.topic.trim(),
        jurisdiction: this.jurisdiction.trim() || undefined,
      });
    },
    goBack() {
      this.$store.commit("Viq/SET_REPORT_STEP", 0);
      this.$store.commit("Viq/SET_REPORT_TYPE", null);
    },
    reset() {
      this.started = false;
      this.topic = "";
      this.jurisdiction = "";
      this.$store.commit("Viq/SET_TIMELINE_EVENTS", []);
    },
    exportPdf() {
      this.$emit("export-pdf");
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "timeline",
        events: this.timelineEvents,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-timeline {
  max-width: 720px;
  margin: 0 auto;
  width: 100%;
  padding: $viq-space-lg 0;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $viq-space-lg;
  }

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0;
  }

  &__desc {
    font-size: $viq-font-base;
    color: $viq-text-secondary;
    margin: 0 0 $viq-space-lg;
  }

  &__form {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-lg;
  }

  &__form-row {
    display: flex;
    gap: $viq-space-sm;
    margin-top: $viq-space-md;
    align-items: flex-start;
  }

  &__events {
    position: relative;
    padding-left: $viq-space-xl;

    &::before {
      content: "";
      position: absolute;
      left: 11px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: $viq-border;
    }
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-md 0;
    color: $viq-text-secondary;
    font-size: $viq-font-sm;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__empty {
    padding: $viq-space-xl;
    text-align: center;
    color: $viq-text-secondary;
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
    margin-top: $viq-space-lg;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
